import React, { useState } from "react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { Check, Loader2, ChevronDown, ChevronUp } from "lucide-react";

interface VoucherRedemptionProps {
  availableVoucherSeats: number;
  attendeeCount: number;
  onVoucherApplied: (registrationsCovered: number) => void;
  onVoucherRemoved: () => void;
  isVoucherApplied: boolean;
}

export default function VoucherRedemption({
  availableVoucherSeats,
  attendeeCount,
  isVoucherApplied,
  onVoucherApplied,
  onVoucherRemoved,
}: VoucherRedemptionProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [voucherQuantity, setVoucherQuantity] = useState<number>(1);
  const [isExpanded, setIsExpanded] = useState(false);
  const { toast } = useToast();

  const handleRedeemVoucher = () => {
    setIsLoading(true);
    if (voucherQuantity > availableVoucherSeats) {
      setError("Not enough voucher seats available.");
      setIsLoading(false);
      return;
    }
    onVoucherApplied(voucherQuantity);
    setIsLoading(false);
    toast({
      title: "Voucher applied!",
      description: `${voucherQuantity} voucher seat(s) applied.`,
    });
  };

  const handleRemoveVoucher = () => {
    onVoucherRemoved();
  };

  if (isVoucherApplied) {
    return (
      <div className="flex items-center gap-2 text-sm py-2">
        <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
        <span className="text-green-700 dark:text-green-300 font-medium">
          Voucher applied
        </span>
        <button
          className="ml-auto text-sm text-muted-foreground underline hover:no-underline"
          onClick={handleRemoveVoucher}
          type="button"
        >
          Remove
        </button>
      </div>
    );
  }

  return (
    <div>
      <button
        className="flex items-center gap-1.5 text-sm text-primary hover:text-primary/80 font-medium py-1"
        onClick={() => setIsExpanded(!isExpanded)}
        type="button"
      >
        {isExpanded ? (
          <ChevronUp className="h-3.5 w-3.5" />
        ) : (
          <ChevronDown className="h-3.5 w-3.5" />
        )}
        Have a voucher? ({availableVoucherSeats} available)
      </button>

      {isExpanded && (
        <div className="mt-3 space-y-3 pl-5">
          <p className="text-xs text-muted-foreground">
            Each voucher seat covers 1 registration.
          </p>
          <div className="flex items-center gap-2">
            <Label className="sr-only" htmlFor="voucher-quantity">
              Voucher seats
            </Label>
            <Input
              className="w-20 h-9"
              disabled={isLoading || availableVoucherSeats === 0}
              id="voucher-quantity"
              max={Math.min(availableVoucherSeats, attendeeCount)}
              min={1}
              onChange={(e) =>
                setVoucherQuantity(
                  Math.max(
                    1,
                    Math.min(
                      parseInt(e.target.value) || 1,
                      Math.min(availableVoucherSeats, attendeeCount),
                    ),
                  ),
                )
              }
              type="number"
              value={voucherQuantity}
            />
            <span className="text-xs text-muted-foreground">
              of {Math.min(availableVoucherSeats, attendeeCount)} max
            </span>
            <Button
              className="ml-auto"
              disabled={isLoading || availableVoucherSeats === 0}
              onClick={handleRedeemVoucher}
              size="sm"
              variant="outline"
            >
              {isLoading ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                "Apply"
              )}
            </Button>
          </div>

          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </div>
      )}
    </div>
  );
}
